import styled from '@emotion/styled'
import { memo, useEffect, useLayoutEffect, useReducer, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import Box from '../components/Box'
import RegisterStepsHeader from '../components/RegisterStepsHeader'
import CourseRegistrationStep from '../components/CourseRegistrationStep'
import RegistrationQrFab from '../components/RegistrationQrFab'
import { useActivity } from '../hooks/useActivity'
import { registerReducer } from '../libs/RegisterReducer'
import { useRegisterActionFunctions } from '../hooks/useRegisterActions'
import { DEFAULT_STATE } from '../hooks/useAppState'
import useAppState from '../hooks/useAppState'
import { REGISTER_PAGE_MAX_WIDTH } from '../enums/HardCodedValues'
import { SIXTEEN_PX, TEN_PX } from '../enums/Spacings'
import { resolveReturnRegisterStep } from '../helpers/resolveReturnRegisterStep'
import { isActivityQueryPending } from '../helpers/isActivityQueryPending'
import { stepConfig } from '../helpers/RegisterPageHelper'
import { shouldShowQrPaymentPendingBadge } from '../helpers/qrPaymentPendingHelper'
import EmptyActivity from '../components/EmptyActivity'
import RegisterPageSkeleton from '../components/RegisterPageSkeleton'
import { hideRegisterBootSkeleton } from '../helpers/isRegisterPagePath'
import { isGetObfuscatedEnrollmentResponse, useObfuscatedEnrollment } from '../libs/ReactQueryHooks'
import useIsMobile from '../hooks/useIsMobile'
import { useRegistrationQrCodes } from '../hooks/useRegistrationQrCodes'
import {
  Client,
  EnrollmentPaymentStatus,
  EnrollmentStatus,
  ActivityRegistrationStatus,
  ActivityStatus,
  EnrollmentPublic
} from '../../../pure-js/types/GrooverTypes'
import PreSelectedActivity from '../components/PreSelectedActivity'
import DeletedEnrollment from '../components/DeletedEnrollment'
import { useClient } from '../hooks/useClient'
import RegistrationStatusButton from '../components/RegistrationStatusButton'
import DropinOnlyStatusButton from '../components/DropinOnlyStatusButton'
import { Button } from '@mui/material'
import BlockIcon from '@mui/icons-material/Block'
import { getGrooverText } from '../libs/GrooverTextRepository'
import { useEnrollmentPublic, getClientBySlug, getActivity } from '../libs/DBApiHandler'
import { isSupportedLanguage, persistLanguageToLocalStateStorage } from '../libs/languagePersistence'
import PublicLanguageToggle from '../components/PublicLanguageToggle'
import RegistrationQueueNotice from '../components/RegistrationQueueNotice'
import { useActivityPublicCapacity } from '../hooks/useActivityPublicCapacity'
import { useCustomer } from '../context/CustomerContext'
import { isActivityFree } from '../utils/activityUtils'
import { calculateDiscountData } from '../hooks/useDiscountCalculations'
import { useSchoolGtm } from '../libs/gtm/useSchoolGtm'
import { useGtmRegistrationEvents } from '../libs/gtm/useGtmRegistrationEvents'

const RegisterPage = () => {
  const { activityId, enrollmentId, clientSlug, activitySlug } = useParams<{
    activityId?: string
    enrollmentId?: string
    clientSlug?: string
    activitySlug?: string
  }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { state: appState, setState: setAppState } = useAppState()

  // For SEO routes (/:clientSlug/register/:activitySlug), resolve slug to a real activityId
  const isSeoRoute = !!clientSlug && !!activitySlug
  const [effectiveActivityId, setEffectiveActivityId] = useState(isSeoRoute ? '' : (activityId ?? ''))
  const [isSeoResolving, setIsSeoResolving] = useState(isSeoRoute)
  const [resolvedClient, setResolvedClient] = useState<Client | null>(null)

  useEffect(() => {
    if (!isSeoRoute) return
    let isMounted = true

    const resolve = async () => {
      const clientData = await getClientBySlug(clientSlug!, () => isMounted)
      if (!clientData || !isMounted) return
      setResolvedClient(clientData)
      const activityData = await getActivity(activitySlug!, () => isMounted, clientData.id)
      if (!isMounted) return
      if (activityData) {
        setEffectiveActivityId(activityData.id)
      }
      setIsSeoResolving(false)
    }

    resolve()
    return () => {
      isMounted = false
    }
  }, [isSeoRoute, clientSlug, activitySlug])

  const { data: activity, isLoading, status: activityStatus } = useActivity(effectiveActivityId, false, enrollmentId)
  const isActivityPending = isActivityQueryPending(effectiveActivityId, activity, activityStatus)
  const { isAtOrOverCapacity } = useActivityPublicCapacity(effectiveActivityId || undefined)
  const [state, dispatcher] = useReducer(registerReducer, DEFAULT_STATE)
  const enrollmentUserId = searchParams.get('u')?.trim() || undefined
  const {
    data: obfuscatedEnrollmentResponse,
    isLoading: isLoadingObfuscatedData,
    refetch: refetchObfuscatedEnrollment
  } = useObfuscatedEnrollment(enrollmentId as string, enrollmentUserId)
  const isHydrating = isLoading || isLoadingObfuscatedData || isSeoResolving || isActivityPending
  const obfuscatedEnrollmentData = isGetObfuscatedEnrollmentResponse(obfuscatedEnrollmentResponse)
    ? obfuscatedEnrollmentResponse.enrollment
    : undefined
  const showRegisterSkeleton = isHydrating && obfuscatedEnrollmentData?.status !== EnrollmentStatus.Deleted
  useLayoutEffect(() => {
    if (!showRegisterSkeleton) hideRegisterBootSkeleton()
  }, [showRegisterSkeleton])

  const activityClient = useClient(activity)
  const isMobile = useIsMobile()
  const { setCustomerId } = useCustomer()
  const actions = useRegisterActionFunctions(dispatcher)
  const { onSetEnrollmentId } = actions
  const discountData = calculateDiscountData({ activity, client: activityClient, state })
  const gtmSelectedActivities = state.obfuscatedEnrollment?.activities?.length
    ? state.obfuscatedEnrollment.activities
    : state.selectedActivities
  const gtmEnrollment =
    obfuscatedEnrollmentData ?? (state.obfuscatedEnrollment?.id ? state.obfuscatedEnrollment : undefined)

  useSchoolGtm(activityClient?.gtmId)
  useGtmRegistrationEvents({
    gtmId: activityClient?.gtmId,
    client: activityClient,
    urlEnrollmentId: enrollmentId,
    stateEnrollmentId: state.enrollmentId || undefined,
    isPaid: state.isPaid,
    paymentStatus: state.paymentStatus,
    primaryActivity: activity,
    selectedActivities: gtmSelectedActivities,
    obfuscatedEnrollment: gtmEnrollment,
    activeRegisterStep: state.activeRegisterStep,
    fallbackValueMajor: discountData.finalPrice
  })

  const { allQRCodes, currentQRData } = useRegistrationQrCodes({
    state,
    enrollmentUserId,
    preloadedEnrollmentData: obfuscatedEnrollmentData ?? null
  })
  const isEnrollmentUnpaidForQrFab = shouldShowQrPaymentPendingBadge({
    activity,
    activitiesForPricing: state.obfuscatedEnrollment?.activities?.length
      ? state.obfuscatedEnrollment.activities
      : state.selectedActivities,
    statePaymentStatus: state.paymentStatus,
    enrollment: obfuscatedEnrollmentData,
    enrollmentUserId,
    client: activityClient
  })

  // Show FAB on Step 4 (unpaid/partly paid) and Step 5 (paid/partly paid confirmed).
  // isPaymentPending is passed separately to control the badge inside the overlay.
  const showRegistrationQrFab =
    activityClient?.showQrCodeBeforePayment === true &&
    (state.activeRegisterStep === 4 || state.activeRegisterStep === 5) &&
    allQRCodes.length > 0 &&
    !!currentQRData

  // Add useEnrollmentPublic for real-time payment status monitoring
  const enrollmentPublicData = useEnrollmentPublic(enrollmentId as string)

  // Customer-specific translations: set customerId (client.id) when the activity's client is loaded.
  // Clear on activity change to avoid leaking previous client terminology during load.
  useEffect(() => {
    setCustomerId(null)
  }, [effectiveActivityId, setCustomerId])

  useEffect(() => {
    if (activityClient?.id) {
      setCustomerId(activityClient.id)
    }
  }, [activityClient?.id, setCustomerId])

  // Read ?lang=sv|en|no and persist it for text helpers
  useEffect(() => {
    const lang = searchParams.get('lang')
    if (!isSupportedLanguage(lang)) return
    persistLanguageToLocalStateStorage(lang)
  }, [searchParams])

  // Keep parent iframe container in sync with dynamic registration content height (Step 2 expands/collapses).
  // Mirrors the pattern used in ActivityDetailContent/PublicActivitiesPage.
  useEffect(() => {
    const getIframeId = () => window.frameElement?.id || 'unknown'

    const sendHeightToParent = () => {
      const height =
        document.documentElement.scrollHeight ||
        document.body.scrollHeight ||
        Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.offsetHeight
        )
      const iframeId = getIframeId()
      const message = { type: 'adjustHeight', height, iframeId }
      window.parent.postMessage(message, '*')
    }

    // Run once on mount + after the first layout paint.
    sendHeightToParent()
    requestAnimationFrame(() => {
      requestAnimationFrame(() => sendHeightToParent())
    })

    window.addEventListener('resize', sendHeightToParent)

    const observer = new MutationObserver(sendHeightToParent)
    observer.observe(document.body, { childList: true, subtree: true })

    const messageHandler = (event: MessageEvent) => {
      const { type } = (event.data || {}) as { type?: string }
      if (type === 'requestHeightUpdate') {
        sendHeightToParent()
      }
    }
    window.addEventListener('message', messageHandler)

    return () => {
      window.removeEventListener('resize', sendHeightToParent)
      window.removeEventListener('message', messageHandler)
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    // skip if enrollment already paid, or obfuscated enrollment is still loading
    if ([null, 'paid'].includes(obfuscatedEnrollmentData?.paymentStatus || null)) {
      return
    }
    if (enrollmentId && enrollmentPublicData.status === 'success' && enrollmentPublicData.data) {
      const { paymentStatus } = enrollmentPublicData.data as EnrollmentPublic
      if (paymentStatus === 'paid') {
        // this should fetch enrollment with payment status paid
        // which should update state.isPaid
        // which should pass user to step5
        console.log('live payment status is', paymentStatus, ', re-fetching obfuscated enrollment')
        actions.onSetPaymentStatus(paymentStatus)
        refetchObfuscatedEnrollment()
      }
    }
  }, [enrollmentPublicData.status, enrollmentPublicData.data, enrollmentId, obfuscatedEnrollmentData])

  useEffect(() => {
    if (enrollmentId) {
      onSetEnrollmentId(enrollmentId)
    }
  }, [enrollmentId, onSetEnrollmentId])

  useEffect(() => {
    if (!enrollmentId || !obfuscatedEnrollmentData || !activity) return

    actions.onSetObfuscatedEnrollment(obfuscatedEnrollmentData)

    if (obfuscatedEnrollmentData.paymentStatus === EnrollmentPaymentStatus.Paid) {
      actions.onSetPaymentStatus(EnrollmentPaymentStatus.Paid)
    } else if (obfuscatedEnrollmentData.paymentStatus === EnrollmentPaymentStatus.PartlyPaid) {
      actions.onSetPaymentStatus(EnrollmentPaymentStatus.PartlyPaid)
    }

    const returnStep = resolveReturnRegisterStep(obfuscatedEnrollmentData, activity, state.selectedActivities)
    actions.onOpenStep(returnStep)
  }, [enrollmentId, obfuscatedEnrollmentData, activity, state.isPaid, state.selectedActivities])

  // Free events hide step 4 in the accordion; redirect orphaned step 4 to the final step.
  useEffect(() => {
    if (!activity || state.activeRegisterStep !== 4) return

    const activitiesForPricing = state.obfuscatedEnrollment?.activities?.length
      ? state.obfuscatedEnrollment.activities
      : state.selectedActivities

    if (isActivityFree(activity, activitiesForPricing)) {
      actions.onOpenStep(5)
    }
  }, [activity, state.activeRegisterStep, state.obfuscatedEnrollment, state.selectedActivities])

  if (obfuscatedEnrollmentData?.status === EnrollmentStatus.Deleted)
    return <DeletedEnrollment client={activityClient} />
  if (showRegisterSkeleton) return <RegisterPageSkeleton />
  if (!activity) return <EmptyActivity client={resolvedClient} />

  // Only check registration status for new registrations (no enrollmentId)
  if (!enrollmentId && activity.status === ActivityStatus.Cancelled) {
    return (
      <Container fullWidth fullHeight fullPadding spacing={TEN_PX} gap={SIXTEEN_PX} align="center" justify="center">
        <Button
          variant="contained"
          disabled
          fullWidth
          startIcon={<BlockIcon />}
          sx={{
            backgroundColor: '#f5f5f5',
            color: '#757575',
            border: '1px solid #bdbdbd',
            fontWeight: 'bold',
            letterSpacing: 1,
            boxShadow: 'none',
            '& .MuiButton-startIcon': { color: '#757575' },
            '&.Mui-disabled': {
              backgroundColor: '#f5f5f5',
              color: '#757575',
              border: '1px solid #bdbdbd',
              opacity: 1
            }
          }}
        >
          {getGrooverText('userWeb.register.activityCancelled')}
        </Button>
      </Container>
    )
  }

  if (!enrollmentId && activity.dropinOnly) {
    return (
      <Container fullWidth fullHeight fullPadding spacing={TEN_PX} gap={SIXTEEN_PX} align="center" justify="center">
        <DropinOnlyStatusButton />
      </Container>
    )
  }

  if (
    !enrollmentId &&
    (activity.registrationStatus === ActivityRegistrationStatus.Closed ||
      activity.registrationStatus === ActivityRegistrationStatus.Full)
  ) {
    return (
      <Container fullWidth fullHeight fullPadding spacing={TEN_PX} gap={SIXTEEN_PX} align="center" justify="center">
        <RegistrationStatusButton registrationStatus={activity.registrationStatus} />
      </Container>
    )
  }

  return (
    <Container fullWidth fullHeight fullPadding spacing={TEN_PX} gap={SIXTEEN_PX}>
      <Box fullWidth direction="row" justify="flex-end" align="center">
        <PublicLanguageToggle
          onLanguageChange={(language) => {
            const next = new URLSearchParams(searchParams)
            next.set('lang', language)
            setSearchParams(next, { replace: true })
          }}
        />
      </Box>

      {!enrollmentId && isAtOrOverCapacity && <RegistrationQueueNotice language={appState.user?.language} />}

      {/* Only show step navigation and preselected activities when not on StepZero */}
      {state.activeRegisterStep > 0 && (
        <>
          <RegisterStepsHeader
            state={state}
            activity={activity}
            activeRegisterStep={state.activeRegisterStep}
            isRegistrationCompleted={state.isRegistrationCompleted}
            onOpenStep={actions.onOpenStep}
          />

          {/*  Only show preselected activities during first steps of registration */}
          {activity && !obfuscatedEnrollmentData && <PreSelectedActivity activity={activity} />}
        </>
      )}

      <CourseRegistrationStep stepConfig={stepConfig} state={state} activity={activity} actions={actions} />

      {showRegistrationQrFab && <FabSpacer />}

      {showRegistrationQrFab && currentQRData && (
        <RegistrationQrFab qrData={currentQRData} isPaymentPending={isEnrollmentUnpaidForQrFab} />
      )}
    </Container>
  )
}

const Container = styled(Box)`
  max-width: ${REGISTER_PAGE_MAX_WIDTH};
`

/** Pushes content above the fixed FAB bar so it is never hidden on scroll. */
const FabSpacer = styled.div`
  height: calc(72px + env(safe-area-inset-bottom, 0px));
  flex-shrink: 0;
`

export default memo(RegisterPage)
